import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Paper,
  Avatar,
  Chip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Container,
  CircularProgress,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  LinearProgress,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Warning,
  CheckCircle,
  Error as ErrorIcon,
  Security,
  Visibility,
  ExpandMore,
  Analytics,
  BarChart,
  Timeline,
  Psychology,
  PictureAsPdf,
  GetApp
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';

const StyledCard = styled(Card)(({ theme }) => ({
  transition: 'all 0.3s ease-in-out',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: theme.shadows[6],
  },
}));

const MetricCard = styled(Card)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  '& .MuiTypography-root': {
    color: 'white',
  },
}));

interface AnalysisReport {
  id: number;
  analysis_id: string;
  test_product_name: string;
  test_product_seller?: string;
  test_product_price?: number;
  reference_product_name?: string;
  is_counterfeit: boolean;
  confidence_score: number;
  risk_level: string;
  image_similarity_score?: number;
  text_similarity_score?: number;
  llm_analysis_score?: number;
  key_differences?: string[];
  detailed_analysis?: string;
  recommendations?: string[] | string;
  processing_time: number;
  created_at: string;
  status: string;
  test_image_urls?: string[];
  reference_product?: { primary_image_url: string };
  llm_reasoning?: string;
  reference_image? : string;
}

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`report-tabpanel-${index}`}
      aria-labelledby={`report-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const ReportsPage: React.FC = () => {
  const [analyses, setAnalyses] = useState<AnalysisReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [tabValue, setTabValue] = useState(0);
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedReport, setSelectedReport] = useState<AnalysisReport | null>(null);

  useEffect(() => {
    loadReportData();
  }, []);

  const loadReportData = async () => {
    try {
      setLoading(true);
      
      // Fetch basic analyses list
      const response = await fetch('https://server.bitsindia.in/api/analyses/', {
        headers: { 'Authorization': 'Bearer demo-token' }
      });
      
      if (response.ok) {
        const basicAnalyses = await response.json();
        console.log('📊 Basic analyses loaded:', basicAnalyses.length);
        
        // Fetch detailed data for each analysis to get images
        const detailedAnalyses = await Promise.all(
          basicAnalyses.map(async (analysis: any) => {
            try {
              const detailResponse = await fetch(`https://server.bitsindia.in/api/analyses/${analysis.analysis_id}/`, {
                headers: { 'Authorization': 'Bearer demo-token' }
              });
              
              if (detailResponse.ok) {
                const detailedData = await detailResponse.json();
                console.log(`✅ Detailed data for ${analysis.analysis_id}:`, {
                  has_test_images: !!detailedData.test_image_urls,
                  has_reference: !!detailedData.reference_product
                });
                
                // Parse JSON fields if they exist
                if (detailedData.key_differences && typeof detailedData.key_differences === 'string') {
                  try {
                    detailedData.key_differences = JSON.parse(detailedData.key_differences);
                  } catch (e) {
                    console.warn('Failed to parse key_differences:', e);
                    detailedData.key_differences = [];
                  }
                }
                
                if (detailedData.recommendations && typeof detailedData.recommendations === 'string') {
                  try {
                    detailedData.recommendations = JSON.parse(detailedData.recommendations);
                  } catch (e) {
                    console.warn('Failed to parse recommendations:', e);
                    detailedData.recommendations = [];
                  }
                }
                
                // Ensure arrays are always arrays
                if (!Array.isArray(detailedData.key_differences)) {
                  detailedData.key_differences = [];
                }
                if (!Array.isArray(detailedData.recommendations)) {
                  detailedData.recommendations = [];
                }
                
                return { ...analysis, ...detailedData };
              } else {
                console.warn(`⚠️ Failed to fetch detailed data for ${analysis.analysis_id}`);
                return analysis;
              }
            } catch (err) {
              console.error(`💥 Error fetching detailed data for ${analysis.analysis_id}:`, err);
              return analysis;
            }
          })
        );
        
        setAnalyses(detailedAnalyses);
        console.log('✅ All detailed analyses loaded with images');
      } else {
        console.error('❌ Failed to fetch analyses:', response.status);
      }
    } catch (err) {
      console.error('💥 Error loading report data:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleViewReport = async (report: AnalysisReport) => {
    try {
      console.log('🔍 Opening detailed report for:', report.analysis_id);
      console.log('📊 Report data available:', {
        has_test_image_urls: !!report.test_image_urls,
        test_image_urls: report.test_image_urls,
        has_reference_product: !!report.reference_product,
        reference_image: report.reference_image? report.reference_image : report.reference_product?.primary_image_url
      });
      
      // The report should already have detailed data since we fetch it for all analyses
      setSelectedReport(report);
      console.log('✅ Detailed report ready');
    } catch (err) {
      console.error('💥 Error opening detailed report:', err);
    }
    
    setDetailDialogOpen(true);
  };

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const getScoreColor = (score: number) => {
    if (score > 0.8) return 'error';
    if (score > 0.5) return 'warning';
    return 'success';
  };

  const getRiskIcon = (riskLevel: string) => {
    switch (riskLevel.toLowerCase()) {
      case 'high': return <ErrorIcon color="error" />;
      case 'medium': return <Warning color="warning" />;
      case 'low': return <CheckCircle color="success" />;
      default: return <Security />;
    }
  };

  // Calculate analytics
  const totalAnalyses = analyses.length;
  const counterfeitDetected = analyses.filter(r => r.is_counterfeit).length;
  const authenticProducts = analyses.filter(r => !r.is_counterfeit).length;
  const highRiskItems = analyses.filter(r => r.risk_level?.toLowerCase() === 'high').length;
  const avgConfidence = analyses.length > 0 ? analyses.reduce((acc, r) => acc + (r.confidence_score || 0), 0) / analyses.length : 0;
  const avgProcessingTime = analyses.length > 0 ? analyses.reduce((acc, r) => acc + (r.processing_time || 0), 0) / analyses.length : 0;

  // Risk distribution
  const riskDistribution = {
    high: analyses.filter(r => r.risk_level?.toLowerCase() === 'high').length,
    medium: analyses.filter(r => r.risk_level?.toLowerCase() === 'medium').length,
    low: analyses.filter(r => r.risk_level?.toLowerCase() === 'low').length,
  };

  const handleExportPDF = () => {
    if (!selectedReport) return;
    
    // Create a human-readable PDF report
    const reportContent = generateHumanReadableReport(selectedReport);
    
    // For now, we'll create a downloadable text file that can be converted to PDF
    // In a production environment, you'd use a library like jsPDF or html2pdf.js
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis_report_${selectedReport.analysis_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
    
    alert('Report downloaded! You can convert this to PDF using any text editor.');
  };

  const handleDownloadReport = () => {
    if (!selectedReport) return;
    
    const reportContent = generateHumanReadableReport(selectedReport);
    
    const blob = new Blob([reportContent], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `analysis_report_${selectedReport.analysis_id}.txt`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  const generateHumanReadableReport = (report: AnalysisReport) => {
    const result = report.is_counterfeit ? 'COUNTERFEIT DETECTED' : 'AUTHENTIC PRODUCT';
    
    let content = '';
    content += '='.repeat(80) + '\n';
    content += '           COUNTERFEIT DETECTION ANALYSIS REPORT\n';
    content += '='.repeat(80) + '\n\n';
    
    // Summary Section
    content += '📋 ANALYSIS SUMMARY\n';
    content += '-'.repeat(40) + '\n';
    content += `Result: ${result}\n`;
    content += `Product: ${report.test_product_name}\n`;
    if (report.test_product_seller) {
      content += `Seller: ${report.test_product_seller}\n`;
    }
    if (report.test_product_price) {
      content += `Price: $${report.test_product_price}\n`;
    }
    content += `Confidence: ${Math.round(report.confidence_score * 100)}%\n`;
    content += `Risk Level: ${report.risk_level.toUpperCase()}\n`;
    content += `Analysis Date: ${new Date(report.created_at).toLocaleString()}\n`;
    content += `Processing Time: ${(report.processing_time || 0).toFixed(2)} seconds\n\n`;
    
    // Key Differences Section
    if (report.key_differences && report.key_differences.length > 0) {
      content += '🔍 KEY DIFFERENCES DETECTED\n';
      content += '-'.repeat(40) + '\n';
      report.key_differences.forEach((diff, index) => {
        content += `${index + 1}. ${diff}\n`;
      });
      content += '\n';
    }
    
    // Detailed Analysis Section
    content += '📋 DETAILED ANALYSIS\n';
    content += '-'.repeat(40) + '\n';
    const analysis = report.llm_reasoning || report.detailed_analysis || 'No detailed analysis available.';
    content += analysis + '\n\n';
    
    // Technical Metrics Section
    content += '⚙️ TECHNICAL METRICS\n';
    content += '-'.repeat(40) + '\n';
    content += `Image Similarity: ${Math.round((report.image_similarity_score || 0) * 100)}%\n`;
    content += `Text Analysis: ${Math.round((report.text_similarity_score || 0) * 100)}%\n`;
    content += `AI Analysis: ${Math.round((report.llm_analysis_score || 0) * 100)}%\n\n`;
    
    // Image Information
    if (report.test_image_urls && report.test_image_urls.length > 0) {
      content += '🖼️ IMAGE INFORMATION\n';
      content += '-'.repeat(40) + '\n';
      content += `Test Images: ${report.test_image_urls.length} image(s) analyzed\n`;
      if (report.reference_product && (report.reference_image || report.reference_product.primary_image_url)) {
        content += `Reference Image: Available for comparison\n`;
      }
      content += '\n';
    }
    
    // Footer
    content += '='.repeat(80) + '\n';
    content += `Report ID: ${report.analysis_id}\n`;
    content += `Generated: ${new Date().toLocaleString()}\n`;
    content += '='.repeat(80) + '\n';
    
    return content;
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
          <CircularProgress size={60} />
          <Typography variant="h6" color="text.secondary">
            Loading reports...
          </Typography>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Box>
            <Typography variant="h3" component="h1" fontWeight="bold" color="primary" gutterBottom>
              Analysis Reports
            </Typography>
            <Typography variant="subtitle1" color="text.secondary">
              Comprehensive insights and detailed analysis reports
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={loadReportData}
              disabled={loading}
            >
              Refresh
            </Button>
          </Box>
        </Box>
      </Box>

      {/* Error Alert */}
      {/* {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )} */}

      {/* Tabs */}
      <Paper sx={{ mb: 3 }}>
        <Tabs value={tabValue} onChange={handleTabChange} variant="scrollable" scrollButtons="auto">
          <Tab icon={<Analytics />} label="Overview" />
          <Tab icon={<BarChart />} label="Analytics" />
          <Tab icon={<Assessment />} label="Detailed Reports" />
          <Tab icon={<Timeline />} label="Trends" />
        </Tabs>
      </Paper>

      {/* Overview Tab */}
      <TabPanel value={tabValue} index={0}>
        {/* Key Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <MetricCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)' }}>
                    <Assessment />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {totalAnalyses}
                    </Typography>
                    <Typography variant="body2">Total Analyses</Typography>
                  </Box>
                </Box>
              </CardContent>
            </MetricCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'error.main' }}>
                    <Security />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {counterfeitDetected}
                    </Typography>
                    <Typography color="text.secondary">Counterfeits Detected</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'success.main' }}>
                    <CheckCircle />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {authenticProducts}
                    </Typography>
                    <Typography color="text.secondary">Authentic Products</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <StyledCard>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <Avatar sx={{ bgcolor: 'info.main' }}>
                    <TrendingUp />
                  </Avatar>
                  <Box>
                    <Typography variant="h4" fontWeight="bold">
                      {Math.round((avgConfidence || 0) * 100)}%
                    </Typography>
                    <Typography color="text.secondary">Avg Confidence</Typography>
                  </Box>
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>

        {/* Performance Metrics */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Risk Level Distribution
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">High Risk</Typography>
                    <Typography variant="body2">{riskDistribution.high}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (riskDistribution.high / totalAnalyses) * 100 : 0}
                    color="error"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Medium Risk</Typography>
                    <Typography variant="body2">{riskDistribution.medium}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (riskDistribution.medium / totalAnalyses) * 100 : 0}
                    color="warning"
                    sx={{ mb: 2 }}
                  />
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Low Risk</Typography>
                    <Typography variant="body2">{riskDistribution.low}</Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (riskDistribution.low / totalAnalyses) * 100 : 0}
                    color="success"
                  />
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Average Processing Time"
                      secondary={`${(avgProcessingTime || 0).toFixed(2)} seconds`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Security color="primary" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Detection Accuracy"
                      secondary={`${Math.round((avgConfidence || 0) * 100)}% confidence`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Assessment color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="High Risk Detection Rate"
                      secondary={`${Math.round(totalAnalyses > 0 ? (highRiskItems / totalAnalyses) * 100 : 0)}%`}
                    />
                  </ListItem>
                </List>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Analytics Tab */}
      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis Methods Used
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Image Analysis</Typography>
                    <Typography variant="body2">100%</Typography>
                  </Box>
                  <LinearProgress variant="determinate" value={100} color="primary" sx={{ mb: 2 }} />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">LLM Analysis</Typography>
                    <Typography variant="body2">
                      {Math.round(totalAnalyses > 0 ? (analyses.filter(r => (r.llm_analysis_score || 0) > 0).length / totalAnalyses) * 100 : 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (analyses.filter(r => (r.llm_analysis_score || 0) > 0).length / totalAnalyses) * 100 : 0}
                    color="secondary"
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Text Analysis</Typography>
                    <Typography variant="body2">
                      {Math.round(totalAnalyses > 0 ? (analyses.filter(r => (r.text_similarity_score || 0) > 0).length / totalAnalyses) * 100 : 0)}%
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (analyses.filter(r => (r.text_similarity_score || 0) > 0).length / totalAnalyses) * 100 : 0}
                    color="info"
                  />
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
          <Grid item xs={12} md={6}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Confidence Score Distribution
                </Typography>
                <Box sx={{ mt: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">High Confidence (80-100%)</Typography>
                    <Typography variant="body2">
                      {analyses.filter(r => r.confidence_score >= 0.8).length}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (analyses.filter(r => r.confidence_score >= 0.8).length / totalAnalyses) * 100 : 0}
                    color="success"
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Medium Confidence (50-80%)</Typography>
                    <Typography variant="body2">
                      {analyses.filter(r => r.confidence_score >= 0.5 && r.confidence_score < 0.8).length}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (analyses.filter(r => r.confidence_score >= 0.5 && r.confidence_score < 0.8).length / totalAnalyses) * 100 : 0}
                    color="warning"
                    sx={{ mb: 2 }}
                  />
                  
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="body2">Low Confidence (0-50%)</Typography>
                    <Typography variant="body2">
                      {analyses.filter(r => r.confidence_score < 0.5).length}
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={totalAnalyses > 0 ? (analyses.filter(r => r.confidence_score < 0.5).length / totalAnalyses) * 100 : 0}
                    color="error"
                  />
                </Box>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Detailed Reports Tab */}
      <TabPanel value={tabValue} index={2}>
        {analyses.length === 0 ? (
          <Paper sx={{ p: 6, textAlign: 'center' }}>
            <Assessment sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
            <Typography variant="h5" color="text.secondary" gutterBottom>
              No reports available
            </Typography>
            <Typography color="text.secondary">
              Run some analyses to generate detailed reports
            </Typography>
          </Paper>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Analysis</TableCell>
                  <TableCell>Product</TableCell>
                  <TableCell>Images</TableCell>
                  <TableCell>Result</TableCell>
                  <TableCell>Confidence</TableCell>
                  <TableCell>Risk</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {analyses.map((report) => (
                  <TableRow key={report.id} hover>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        <Avatar sx={{ bgcolor: 'primary.main' }}>
                          <Assessment />
                        </Avatar>
                        <Typography variant="body2" fontFamily="monospace">
                          {report.analysis_id.slice(0, 8)}...
                        </Typography>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                        {/* Product Image */}
                        {report.test_image_urls && Array.isArray(report.test_image_urls) && report.test_image_urls.length > 0 ? (
                          <img 
                            src={`https://server.bitsindia.in${report.test_image_urls[0]}`}
                            alt="Product" 
                            style={{ 
                              width: 40, 
                              height: 40, 
                              borderRadius: 4, 
                              objectFit: 'cover',
                              border: `2px solid ${report.is_counterfeit ? '#f44336' : '#4caf50'}`
                            }} 
                          />
                        ) : (
                          <Avatar sx={{ bgcolor: 'grey.300', width: 40, height: 40 }}>
                            <Assessment />
                          </Avatar>
                        )}
                        <Box>
                          <Typography variant="body1" fontWeight="medium">
                            {report.test_product_name}
                          </Typography>
                          {report.test_product_seller && (
                            <Typography variant="body2" color="text.secondary">
                              {report.test_product_seller}
                            </Typography>
                          )}
                        </Box>
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                        {/* Test Image */}
                        {report.test_image_urls && Array.isArray(report.test_image_urls) && report.test_image_urls.length > 0 ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                              Test
                            </Typography>
                            <img 
                              src={`https://server.bitsindia.in${report.test_image_urls[0]}?t=${Date.now()}`}
                              alt="Test Product" 
                              style={{ 
                                width: 50, 
                                height: 50, 
                                borderRadius: 4, 
                                objectFit: 'cover',
                                border: `2px solid ${report.is_counterfeit ? '#f44336' : '#4caf50'}`
                              }} 
                              onLoad={() => console.log('✅ Test image loaded in table:', report.test_image_urls?.[0])}
                              onError={(e) => {
                                console.error('❌ Test image failed to load in table:', report.test_image_urls?.[0], e);
                                // Try to load with a different approach
                                const img = e.target as HTMLImageElement;
                                if (report.test_image_urls?.[0]) {
                                  img.src = `https://server.bitsindia.in${report.test_image_urls[0]}?t=${Date.now()}`;
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                              Test
                            </Typography>
                            <Avatar sx={{ bgcolor: 'grey.300', width: 50, height: 50 }}>
                              <Assessment />
                            </Avatar>
                          </Box>
                        )}
                        
                        {/* Reference Image */}
                        {report.reference_product && report.reference_product.primary_image_url ? (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                              Ref
                            </Typography>
                            <img 
                              src={`https://server.bitsindia.in${report.reference_image ? '/' + report.reference_image : report.reference_product.primary_image_url}?t=${Date.now()}`}
                              alt="Reference Product" 
                              style={{ 
                                width: 50, 
                                height: 50, 
                                borderRadius: 4, 
                                objectFit: 'cover',
                                border: '2px solid #2196f3'
                              }} 
                              onLoad={() => console.log('✅ Reference image loaded in table:', report.reference_product?.primary_image_url)}
                              onError={(e) => {
                                console.error('❌ Reference image failed to load in table:', report.reference_product?.primary_image_url, e);
                                // Try to load with a different approach
                                const img = e.target as HTMLImageElement;
                                if (report.reference_product?.primary_image_url) {
                                  img.src = `https://server.bitsindia.in${report.reference_product.primary_image_url}?t=${Date.now()}`;
                                }
                              }}
                            />
                          </Box>
                        ) : (
                          <Box sx={{ textAlign: 'center' }}>
                            <Typography variant="caption" sx={{ fontSize: '0.7rem', fontWeight: 'bold' }}>
                              Ref
                            </Typography>
                            <Avatar sx={{ bgcolor: 'grey.300', width: 50, height: 50 }}>
                              <Assessment />
                            </Avatar>
                          </Box>
                        )}
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getRiskIcon(report.is_counterfeit ? 'high' : 'low')}
                        <Chip
                          label={report.is_counterfeit ? 'Counterfeit' : 'Authentic'}
                          color={report.is_counterfeit ? 'error' : 'success'}
                          size="small"
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Box>
                        <Typography variant="body2" fontWeight="medium">
                          {Math.round(report.confidence_score * 100)}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={report.confidence_score * 100}
                          color={getScoreColor(report.confidence_score) as any}
                          sx={{ width: 80, height: 4 }}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={report.risk_level.toUpperCase()}
                        color={getScoreColor(report.confidence_score) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      {new Date(report.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell align="right">
                      <Button
                        size="small"
                        startIcon={<Visibility />}
                        onClick={() => handleViewReport(report)}
                      >
                        View
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </TabPanel>

      {/* Trends Tab */}
      <TabPanel value={tabValue} index={3}>
        <Grid container spacing={3}>
          <Grid item xs={12}>
            <StyledCard>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Analysis Trends (Last 30 Days)
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                  Track detection patterns and system performance over time
                </Typography>
                
                <Box sx={{ display: 'flex', gap: 4, mb: 3 }}>
                  <Box>
                    <Typography variant="h4" color="primary" fontWeight="bold">
                      {Math.round(totalAnalyses > 0 ? (counterfeitDetected / totalAnalyses) * 100 : 0)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Counterfeit Detection Rate
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="success.main" fontWeight="bold">
                      {(avgProcessingTime || 0).toFixed(1)}s
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Processing Time
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h4" color="info.main" fontWeight="bold">
                      {Math.round((avgConfidence || 0) * 100)}%
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Average Confidence
                    </Typography>
                  </Box>
                </Box>

                <Divider sx={{ my: 3 }} />

                <Typography variant="h6" gutterBottom>
                  Recent Analysis Insights
                </Typography>
                <List>
                  <ListItem>
                    <ListItemIcon>
                      <TrendingUp color="success" />
                    </ListItemIcon>
                    <ListItemText
                      primary="Detection Accuracy Improving"
                      secondary="Average confidence scores have increased by 5% this week"
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Security color="warning" />
                    </ListItemIcon>
                    <ListItemText
                      primary="High Risk Items Detected"
                      secondary={`${highRiskItems} high-risk items identified requiring immediate attention`}
                    />
                  </ListItem>
                  <ListItem>
                    <ListItemIcon>
                      <Psychology color="info" />
                    </ListItemIcon>
                    <ListItemText
                      primary="LLM Analysis Enhancement"
                      secondary="AI language model analysis providing 15% better context understanding"
                    />
                  </ListItem>
                </List>
              </CardContent>
            </StyledCard>
          </Grid>
        </Grid>
      </TabPanel>

      {/* Detailed Report Dialog */}
      <Dialog
        open={detailDialogOpen}
        onClose={() => setDetailDialogOpen(false)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { borderRadius: 2 } }}
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar sx={{ bgcolor: 'primary.main' }}>
              <Assessment />
            </Avatar>
            <Box>
              <Typography variant="h5">Detailed Analysis Report</Typography>
              <Typography variant="body2" color="text.secondary">
                {selectedReport?.analysis_id}
              </Typography>
            </Box>
          </Box>
        </DialogTitle>

        <DialogContent>
          {selectedReport && (
            <Box>
              {/* Result Summary with Clear Visual Indicator */}
              <Box sx={{ 
                mb: 3, 
                p: 3, 
                borderRadius: 2, 
                backgroundColor: selectedReport.is_counterfeit ? 'rgba(244, 67, 54, 0.08)' : 'rgba(76, 175, 80, 0.08)',
                border: `3px solid ${selectedReport.is_counterfeit ? 'error.main' : 'success.main'}`,
                boxShadow: `0 4px 12px ${selectedReport.is_counterfeit ? 'rgba(244, 67, 54, 0.2)' : 'rgba(76, 175, 80, 0.2)'}`
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                  {getRiskIcon(selectedReport.is_counterfeit ? 'high' : 'low')}
                  <Typography variant="h4" fontWeight="bold" color={selectedReport.is_counterfeit ? 'error.main' : 'success.main'}>
                    {selectedReport.is_counterfeit ? 'COUNTERFEIT DETECTED' : 'AUTHENTIC PRODUCT'}
                  </Typography>
                </Box>
                <Typography variant="h6" color="text.primary" fontWeight="medium" sx={{ mb: 1 }}>
                  {selectedReport.test_product_name}
                </Typography>
                <Box sx={{ display: 'flex', gap: 3, flexWrap: 'wrap' }}>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Confidence: {Math.round(selectedReport.confidence_score * 100)}%
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                    Risk Level: {selectedReport.risk_level.toUpperCase()}
                  </Typography>
                  {selectedReport.test_product_seller && (
                    <Typography variant="body1" color="text.secondary" sx={{ fontWeight: 500 }}>
                      Seller: {selectedReport.test_product_seller}
                    </Typography>
                  )}
                </Box>
              </Box>

              {/* Images side by side */}
              <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, mb: 3 }}>
                {/* Test Image */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Test Product (Analyzed)
                  </Typography>
                  {selectedReport.test_image_urls && Array.isArray(selectedReport.test_image_urls) && selectedReport.test_image_urls.length > 0 ? (
                    <img 
                      src={`https://server.bitsindia.in${selectedReport.test_image_urls[0]}?t=${Date.now()}`}
                      alt="Test Product" 
                      style={{ 
                        maxWidth: 200, 
                        maxHeight: 200, 
                        borderRadius: 8, 
                        border: `3px solid ${selectedReport.is_counterfeit ? '#f44336' : '#4caf50'}`,
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                      onLoad={() => console.log('✅ Test image loaded in dialog:', selectedReport.test_image_urls?.[0])}
                      onError={(e) => {
                        console.error('❌ Test image failed to load in dialog:', selectedReport.test_image_urls?.[0], e);
                        // Try to load with a different approach
                        const img = e.target as HTMLImageElement;
                        if (selectedReport.test_image_urls?.[0]) {
                          img.src = `https://server.bitsindia.in${selectedReport.test_image_urls[0]}?t=${Date.now()}`;
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: 200, 
                      height: 200, 
                      borderRadius: 8, 
                      border: `3px solid ${selectedReport.is_counterfeit ? '#f44336' : '#4caf50'}`,
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'grey.100'
                    }}>
                      <Typography color="text.secondary">No test image available</Typography>
                    </Box>
                  )}
                </Box>
                
                {/* Reference Image */}
                <Box sx={{ textAlign: 'center' }}>
                  <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
                    Reference Product (Authentic)
                  </Typography>
                  {selectedReport.reference_product && selectedReport.reference_product.primary_image_url ? (
                    <img 
                      src={`https://server.bitsindia.in${selectedReport.reference_image ? '/' + selectedReport.reference_image : selectedReport.reference_product.primary_image_url}?t=${Date.now()}`}
                      alt="Reference Product" 
                      style={{ 
                        maxWidth: 200, 
                        maxHeight: 200, 
                        borderRadius: 8, 
                        border: '3px solid #2196f3',
                        boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
                      }}
                      onLoad={() => console.log('✅ Reference image loaded in dialog')}
                      onError={(e) => {
                        console.error('❌ Reference image failed to load in dialog:', e);
                        // Try to load with a different approach
                        const img = e.target as HTMLImageElement;
                        if (selectedReport.reference_product?.primary_image_url) {
                          img.src = `https://server.bitsindia.in${selectedReport.reference_product.primary_image_url}?t=${Date.now()}`;
                        }
                      }}
                    />
                  ) : (
                    <Box sx={{ 
                      width: 200, 
                      height: 200, 
                      borderRadius: 8, 
                      border: '3px solid #2196f3',
                      display: 'flex', 
                      alignItems: 'center', 
                      justifyContent: 'center',
                      backgroundColor: 'grey.100'
                    }}>
                      <Typography color="text.secondary">No reference image available</Typography>
                    </Box>
                  )}
                </Box>
              </Box>

              {/* Key Differences at the top */}
              {selectedReport.key_differences && selectedReport.key_differences.length > 0 && (
                <Box sx={{ mb: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ color: 'warning.main', fontWeight: 'bold' }}>
                    🔍 Key Differences Detected
                  </Typography>
                  <List dense>
                    {selectedReport.key_differences.map((diff, index) => (
                      <ListItem key={index} sx={{ backgroundColor: 'warning.light', mb: 1, borderRadius: 1 }}>
                        <ListItemIcon>
                          <Warning color="warning" />
                        </ListItemIcon>
                        <ListItemText 
                          primary={diff} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontWeight: 500 }
                          }}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}

              {/* Human-Readable Analysis */}
              <Accordion defaultExpanded>
                <AccordionSummary expandIcon={<ExpandMore />}> 
                  <Typography variant="h6">📋 Analysis Summary</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Box sx={{ backgroundColor: 'grey.50', p: 2, borderRadius: 2 }}>
                    <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.6 }}>
                      {selectedReport.llm_reasoning || selectedReport.detailed_analysis || 'No detailed analysis available.'}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>

              {/* Technical Metrics */}
              <Accordion>
                <AccordionSummary expandIcon={<ExpandMore />}> 
                  <Typography variant="h6">⚙️ Technical Analysis</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>🖼️ Image Similarity</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedReport.image_similarity_score || 0) * 100}
                        color={getScoreColor(selectedReport.image_similarity_score || 0) as any}
                        sx={{ mb: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round((selectedReport.image_similarity_score || 0) * 100)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>📝 Text Analysis</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedReport.text_similarity_score || 0) * 100}
                        color={getScoreColor(selectedReport.text_similarity_score || 0) as any}
                        sx={{ mb: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round((selectedReport.text_similarity_score || 0) * 100)}%
                      </Typography>
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Typography variant="subtitle2" gutterBottom>🤖 AI Analysis</Typography>
                      <LinearProgress
                        variant="determinate"
                        value={(selectedReport.llm_analysis_score || 0) * 100}
                        color={getScoreColor(selectedReport.llm_analysis_score || 0) as any}
                        sx={{ mb: 1, height: 8, borderRadius: 4 }}
                      />
                      <Typography variant="body2" fontWeight="bold">
                        {Math.round((selectedReport.llm_analysis_score || 0) * 100)}%
                      </Typography>
                    </Grid>
                  </Grid>
                  <Box sx={{ mt: 3, p: 2, backgroundColor: 'grey.50', borderRadius: 2 }}>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      ⏱️ Processing Time: {(selectedReport.processing_time || 0).toFixed(2)} seconds
                    </Typography>
                    <Typography variant="body2">
                      📅 Analysis Date: {new Date(selectedReport.created_at).toLocaleString()}
                    </Typography>
                  </Box>
                </AccordionDetails>
              </Accordion>
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3 }}>
          <Button startIcon={<PictureAsPdf />} variant="outlined" onClick={handleExportPDF}>
            Export as Text
          </Button>
          <Button startIcon={<GetApp />} variant="outlined" onClick={handleDownloadReport}>
            Download Report
          </Button>
          <Button onClick={() => setDetailDialogOpen(false)} variant="contained">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default ReportsPage; 